import { loadOfficialSdk, type OfficialClient } from './loader'
import { STEP_UP_HEADER, announceRefusal, cachedStepUpToken, dropStepUpToken, refusalCodeOf, requestStepUpToken, stepUpScopeOf } from '../twoFactor/stepUpBridge'

export interface Spec {
  httpMethod: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path: string
  hasBody?: boolean
}

const apiPath = (path: string): string => '/api' + (path.startsWith('/') ? path : '/' + path)

const pathParamsOf = (path: string): string[] =>
  Array.from(path.matchAll(/\{(\w+)\}/g), m => m[1])

// Converts the official MemberfyError into the axios-like error shape the
// app already reads (error.response.status, error.response.data.message).
type SdkFailure = { name?: string; message?: string; status?: number; body?: unknown }
export type LegacyError = Error & { status?: number; response?: { status?: number; data: unknown }; retryAfter?: number }
/** Request arguments as the official client takes them (path params, `body`, `query`, `headers`, `signal`). */
type Args = Record<string, unknown>
type MemberfyGlobal = { createClient: (options: Record<string, unknown>) => OfficialClient }
const memberfyGlobal = (): MemberfyGlobal => (window as unknown as { Memberfy: MemberfyGlobal }).Memberfy

export const toLegacyError = <E>(e: E): E | LegacyError => {
  const failure = e as SdkFailure | null
  if (failure && failure.name === 'MemberfyError') {
    const err: LegacyError = new Error(failure.message || 'Request failed')
    err.status = failure.status
    err.response = { status: failure.status, data: failure.body ?? {} }
    return err
  }
  return e
}

/**
 * Fired when a request that carried the session token comes back 401: the
 * session no longer holds (a token from before F-31 F3, from another
 * community, or one a password change replaced). `detail.message` is the
 * API's own text (`auth.signInAgain`). AuthContext ends the session and
 * opens the login with that message.
 */
export const SESSION_ENDED_EVENT = 'memberfy:session-ended'

/**
 * Only when the token that failed is still the session's: a 401 for a token a
 * sign-in already replaced says nothing about the new session.
 */
const reportUnauthorized = (
  client: OfficialClient,
  sentToken: string | null,
  caught: unknown
) => {
  const e = caught as { status?: number; body?: { message?: string } } | null | undefined
  if (!sentToken || e?.status !== 401 || client.getToken() !== sentToken) return
  window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: { message: e?.body?.message || '' } }))
}

export class SdkContext {
  private readyPromise: Promise<OfficialClient>

  constructor() {
    this.readyPromise = loadOfficialSdk().then(client => {
      /* A 401 is handled in `raw`/`envelope`, which know the token the request carried. */
      client.config.onUnauthorized = null
      return client
    })
  }

  get ready(): Promise<OfficialClient> {
    return this.readyPromise
  }

  async raw<T>(spec: Spec, args: Args = {}): Promise<T> {
    const client = await this.ready
    const finalArgs = { ...args }

    if (spec.hasBody && finalArgs.body === undefined) {
      const reserved = new Set(['headers', 'query', 'body', 'signal'])
      if (Object.keys(finalArgs).every((key) => reserved.has(key))) {
        finalArgs.body = {}
      }
    }

    const sentToken = client.getToken() || null
    try {
      const result = await this.requestWithStepUp(client, spec, finalArgs)

      // The official SDK returns paginated listings as { data, pagination }.
      // Normalize to { items, pagination } so every consumer reads one shape.
      if (
        result &&
        typeof result === 'object' &&
        !Array.isArray(result) &&
        'pagination' in result &&
        'data' in result &&
        Array.isArray((result as { data: unknown }).data)
      ) {
        const paged = result as { data: unknown[]; pagination: unknown }
        return { items: paged.data, pagination: paged.pagination } as T
      }

      return result as T
    } catch (e) {
      reportUnauthorized(client, sentToken, e)
      throw toLegacyError(e)
    }
  }

  /**
   * F-233: one request, and on 403 `STEP_UP_REQUIRED` the same request again with
   * `X-Step-Up-Token` (the tab's token while it lasts, otherwise the code modal's).
   * Any other 2FA refusal only raises its event (`stepUpBridge`).
   */
  private async requestWithStepUp(client: OfficialClient, spec: Spec, args: Args): Promise<unknown> {
    const send = (stepUpToken?: string) => client.request(
      {
        httpMethod: spec.httpMethod,
        path: apiPath(spec.path),
        pathParams: pathParamsOf(spec.path),
        queryParams: [],
        hasBody: spec.hasBody ?? false
      },
      stepUpToken
        ? { ...args, headers: { ...((args.headers as Record<string, string> | undefined) || {}), [STEP_UP_HEADER]: stepUpToken } }
        : args
    )
    const isStepUp = (e: unknown) => {
      const failure = e as SdkFailure | null
      return failure?.status === 403 && refusalCodeOf(failure.body) === 'STEP_UP_REQUIRED'
    }
    try {
      return await send()
    } catch (first) {
      if (!isStepUp(first)) {
        const failure = first as SdkFailure | null
        announceRefusal(failure?.status, failure?.body)
        throw first
      }
      /* The token is reused only for the session, community and member it was issued under. */
      const scope = stepUpScopeOf(client.config)
      const cached = cachedStepUpToken(scope)
      if (cached) {
        try {
          return await send(cached)
        } catch (again) {
          if (!isStepUp(again)) throw again
          dropStepUpToken()
        }
      }
      const fresh = await requestStepUpToken(scope)
      if (!fresh) throw first
      return send(fresh)
    }
  }

  /**
   * The whole response body (`{ success, data, pagination, meta, ... }`), for the few routes whose
   * `meta` the screen needs. The shared client unwraps the envelope and drops `meta` (APP-96), so this
   * borrows a `raw` client that carries the same token, community, member and language.
   */
  async envelope<T>(spec: Spec, args: Args = {}): Promise<T> {
    const main = await this.ready
    const Memberfy = memberfyGlobal()
    const { baseUrl, token, communityId, memberId, language } = main.config
    const client = Memberfy.createClient({ baseUrl, token, communityId, memberId, language, raw: true }) as OfficialClient
    try {
      return await client.request(
        { httpMethod: spec.httpMethod, path: apiPath(spec.path), pathParams: pathParamsOf(spec.path), queryParams: [], hasBody: spec.hasBody ?? false },
        args
      )
    } catch (e) {
      reportUnauthorized(main, token || null, e)
      throw toLegacyError(e)
    }
  }

  /**
   * Like `call`, for the public auth routes (SEC-173): a 429 there carries
   * `Retry-After`, which the shared client drops. This borrows a client with
   * the same community and language whose fetch keeps that header, and puts
   * it on the error as `retryAfter` (seconds).
   */
  async callKeepingRetryAfter<T>(spec: Spec, args: Args = {}): Promise<T> {
    const main = await this.ready
    const Memberfy = memberfyGlobal()
    const { baseUrl, token, communityId, memberId, language } = main.config
    let retryAfter: number | undefined
    const keepingFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const response = await fetch(input, init)
      const header = Number(response.headers.get('Retry-After'))
      retryAfter = Number.isFinite(header) && header > 0 ? header : undefined
      return response
    }
    const client = Memberfy.createClient({ baseUrl, token, communityId, memberId, language, fetch: keepingFetch }) as OfficialClient
    try {
      return await client.request(
        { httpMethod: spec.httpMethod, path: apiPath(spec.path), pathParams: pathParamsOf(spec.path), queryParams: [], hasBody: spec.hasBody ?? false },
        args
      )
    } catch (e) {
      const err = toLegacyError(e)
      if (retryAfter && err instanceof Error) (err as LegacyError).retryAfter = retryAfter
      throw err
    }
  }

  async call<T>(spec: Spec, args: Args = {}): Promise<T> {
    return this.raw<T>(spec, args)
  }
}

export const normalizeLocale = (locale: string): string => {
  const langMap: Record<string, string> = {
    'en': 'en-US',
    'en-US': 'en-US',
    'pt': 'pt-BR',
    'pt-BR': 'pt-BR',
    'pt-PT': 'pt-PT',
    'es': 'es-ES',
    'es-ES': 'es-ES',
    'es-MX': 'es-MX',
    'es-AR': 'es-AR',
    'it': 'it-IT',
    'it-IT': 'it-IT',
    'de': 'de-DE',
    'de-DE': 'de-DE',
    'de-AT': 'de-DE',
    'de-CH': 'de-DE',
    'fr': 'fr-FR',
    'fr-FR': 'fr-FR',
    'fr-BE': 'fr-FR',
    'fr-CA': 'fr-FR',
    'fr-CH': 'fr-FR'
  }

  if (langMap[locale]) return langMap[locale]

  const shortLang = locale.split('-')[0]
  return langMap[shortLang] ?? 'en-US'
}
